// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './QuantumStormEffect.css';

const QuantumStormEffect = ({ isActive, intensity = 1.0, onComplete }) => {
  const [particles, setParticles] = useState([]);
  const [lightningBolts, setLightningBolts] = useState([]);
  const [distortionLevel, setDistortionLevel] = useState(0);

  // Generate quantum particles
  useEffect(() => {
    if (isActive) {
      const particleCount = Math.floor(150 * intensity);
      const newParticles = [];
      
      for (let i = 0; i < particleCount; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 4 + 1,
          speed: Math.random() * 2 + 0.5,
          direction: Math.random() * 360,
          color: Math.random() > 0.5 ? '#ff00ff' : '#8800ff',
          opacity: Math.random() * 0.8 + 0.2,
          phase: Math.random() * Math.PI * 2
        });
      }
      
      setParticles(newParticles);
    } else {
      setParticles([]);
    }
  }, [isActive, intensity]);

  // Generate lightning bolts
  useEffect(() => {
    if (isActive) {
      const generateLightning = () => {
        const boltCount = Math.floor(3 * intensity);
        const newBolts = [];
        
        for (let i = 0; i < boltCount; i++) {
          const points = [];
          const segments = 8 + Math.floor(Math.random() * 6);
          
          // Create jagged lightning path
          for (let j = 0; j <= segments; j++) {
            const progress = j / segments;
            const x = progress * 100 + (Math.random() - 0.5) * 20;
            const y = Math.random() * 100;
            points.push({ x, y });
          }
          
          newBolts.push({
            id: i,
            points,
            color: Math.random() > 0.3 ? '#ff00ff' : '#ffff00',
            width: Math.random() * 3 + 1,
            opacity: Math.random() * 0.8 + 0.2,
            duration: Math.random() * 500 + 200
          });
        }
        
        setLightningBolts(newBolts);
        
        // Clear lightning after duration
        setTimeout(() => {
          setLightningBolts([]);
        }, 800);
      };

      // Generate lightning periodically
      const lightningInterval = setInterval(generateLightning, 1000 + Math.random() * 2000);
      generateLightning(); // Initial lightning
      
      return () => clearInterval(lightningInterval);
    }
  }, [isActive, intensity]);

  // Distortion effect
  useEffect(() => {
    if (isActive) {
      let distortionTimer = 0;
      const distortionInterval = setInterval(() => {
        distortionTimer += 0.1;
        const newDistortion = Math.sin(distortionTimer) * intensity * 0.5 + 0.5;
        setDistortionLevel(newDistortion);
      }, 50);
      
      return () => clearInterval(distortionInterval);
    } else {
      setDistortionLevel(0);
    }
  }, [isActive, intensity]);

  // Auto-complete after duration
  useEffect(() => {
    if (isActive && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 8000); // 8 second effect
      
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);

  if (!isActive) return null;

  // Create SVG path for lightning bolt
  const createLightningPath = (points) => {
    if (points.length < 2) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${points[i].y}`;
    }
    return path;
  };

  return createPortal(
    <div className="quantum-storm-overlay">
      {/* Reality distortion filter */}
      <div 
        className="quantum-distortion"
        style={{
          filter: `hue-rotate(${distortionLevel * 180}deg) saturate(${1 + distortionLevel}) contrast(${1 + distortionLevel * 0.5})`,
          transform: `scale(${1 + distortionLevel * 0.02}) rotate(${distortionLevel * 2}deg)`
        }}
      />
      
      {/* Quantum particles */}
      <div className="quantum-particles">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="quantum-particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              opacity: particle.opacity,
              animationDelay: `${particle.phase}s`,
              animationDuration: `${2 + Math.random()}s`
            }}
          />
        ))}
      </div>
      
      {/* Lightning bolts */}
      <svg className="quantum-lightning" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <filter id="lightning-glow">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {lightningBolts.map(bolt => (
          <path
            key={bolt.id}
            d={createLightningPath(bolt.points)}
            stroke={bolt.color}
            strokeWidth={bolt.width}
            fill="none"
            opacity={bolt.opacity}
            filter="url(#lightning-glow)"
            className="lightning-bolt"
          />
        ))}
      </svg>
      
      {/* Quantum field ripples */}
      <div className="quantum-ripples">
        <div className="quantum-ripple ripple-1" />
        <div className="quantum-ripple ripple-2" />
        <div className="quantum-ripple ripple-3" />
      </div>
      
      {/* Energy vortex */}
      <div className="quantum-vortex">
        <div className="vortex-ring ring-1" />
        <div className="vortex-ring ring-2" />
        <div className="vortex-ring ring-3" />
        <div className="vortex-ring ring-4" />
      </div>
      
      {/* Screen glitch effect */}
      <div className="quantum-glitch">
        <div className="glitch-line glitch-1" />
        <div className="glitch-line glitch-2" />
        <div className="glitch-line glitch-3" />
      </div>
    </div>,
    document.body
  );
};

export default QuantumStormEffect;